import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  Modal,
  Pagination,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  canStopMigrationJob,
  canUndoMigrationJob,
  formatMigrationDate,
  formatMigrationJobError,
  formatMigrationJobProgress,
  MIGRATION_STATUS_FILTER_OPTIONS,
  MIGRATION_STEP_LABELS,
  MIGRATION_TYPE_FILTER_OPTIONS,
  shouldPollMigrationJobs,
} from "../../helpers/migrationJobUi";

const POLL_MS = 3000;

const MigrationJobsPanel = ({ api, onJobsChange, refreshToken = 0 }) => {
  const [jobs, setJobs] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [detailJob, setDetailJob] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowPage, setRowPage] = useState(1);
  const [rowPages, setRowPages] = useState(1);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const apiRef = useRef(api);
  const onJobsChangeRef = useRef(onJobsChange);
  apiRef.current = api;
  onJobsChangeRef.current = onJobsChange;

  const loadJobs = useCallback(async () => {
    const [result, allResult] = await Promise.all([
      apiRef.current.DataMigration.listBatches(page, 20, {
        step: typeFilter,
        status: statusFilter,
      }),
      apiRef.current.DataMigration.listBatches(1, 100),
    ]);
    if (!result?.success) return;
    const docs = result.data?.docs || [];
    setJobs(docs);
    setPages(result.data?.pages || 1);
    if (onJobsChangeRef.current) {
      onJobsChangeRef.current(allResult?.data?.docs || docs);
    }
  }, [page, refreshToken, typeFilter, statusFilter]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const polling = useMemo(() => shouldPollMigrationJobs(jobs), [jobs]);

  useEffect(() => {
    if (!polling) return undefined;
    const timer = setInterval(loadJobs, POLL_MS);
    return () => clearInterval(timer);
  }, [polling, loadJobs]);

  const loadRows = useCallback(
    async (job, nextPage = 1, nextErrorsOnly = errorsOnly) => {
      if (!job?._id) return;
      const result = await apiRef.current.DataMigration.getBatchRows(
        job._id,
        nextPage,
        25,
        nextErrorsOnly
      );
      if (!result?.success) return;
      setRows(result.data?.docs || []);
      setRowPages(result.data?.pages || 1);
      setRowPage(nextPage);
    },
    [errorsOnly]
  );

  const openDetail = async (job) => {
    setDetailJob(job);
    setErrorsOnly(false);
    setRowPage(1);
    await loadRows(job, 1, false);
  };

  const handleErrorsOnly = async (checked) => {
    setErrorsOnly(checked);
    await loadRows(detailJob, 1, checked);
  };

  const handleStop = async (job) => {
    setBusyId(job._id);
    try {
      const result = await apiRef.current.DataMigration.stopBatch(job._id);
      if (!result.success) throw new Error(result.message || "Stop failed");
      showNotification({ title: "Stop requested", message: "Remaining rows will be cancelled." });
      await loadJobs();
    } catch (error) {
      showNotification({ color: "red", title: "Stop failed", message: error.message });
    } finally {
      setBusyId("");
    }
  };

  const handleUndo = async (job) => {
    setBusyId(job._id);
    try {
      const result = await apiRef.current.DataMigration.undoBatch(job._id);
      if (!result.success) throw new Error(result.message || "Undo failed");
      showNotification({ title: "Undo started", message: "Policy records from this job are being removed." });
      await loadJobs();
    } catch (error) {
      showNotification({ color: "red", title: "Undo failed", message: error.message });
    } finally {
      setBusyId("");
    }
  };

  return (
    <>
      <Card withBorder padding="lg">
        <Stack spacing="sm">
          <Group position="apart">
            <Title order={5}>Migration jobs</Title>
            <Button size="xs" variant="light" onClick={loadJobs}>
              Refresh
            </Button>
          </Group>
          <Text size="sm" color="dimmed">
            Jobs stay visible if you leave this page. Stop a running import or
            undo a finished policy job.
          </Text>
          <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
            <Select
              label="Type"
              data={MIGRATION_TYPE_FILTER_OPTIONS}
              value={typeFilter}
              onChange={(value) => {
                setTypeFilter(value || "all");
                setPage(1);
              }}
              withinPortal
            />
            <Select
              label="Status"
              data={MIGRATION_STATUS_FILTER_OPTIONS}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value || "all");
                setPage(1);
              }}
              withinPortal
            />
          </SimpleGrid>
          {jobs.length === 0 ? (
            <Text size="sm" color="dimmed">
              No migrations yet.
            </Text>
          ) : (
            <Table fontSize="xs" striped>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>File</th>
                  <th>Started At</th>
                  <th>Status</th>
                  <th>Completed At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job._id}>
                    <td>{MIGRATION_STEP_LABELS[job.step] || job.step}</td>
                    <td>{job.fileName || "-"}</td>
                    <td>{formatMigrationDate(job.startedAt || job.createdAt)}</td>
                    <td>
                      <Stack spacing={2}>
                        <Badge
                          color={
                            job.status === "FAILED"
                              ? "red"
                              : job.status === "PROCESSING" || job.status === "UNDOING"
                                ? "yellow"
                                : job.status === "CANCELLED"
                                  ? "gray"
                                  : "teal"
                          }
                        >
                          {job.status}
                        </Badge>
                        {(job.status === "PROCESSING" ||
                          job.status === "UNDOING") && (
                          <Group spacing={6} noWrap>
                            <Loader size={12} />
                            <Text size="xs" color="dimmed">
                              {formatMigrationJobProgress(job)}
                            </Text>
                          </Group>
                        )}
                        {job.status === "FAILED" && formatMigrationJobError(job) && (
                          <Text size="xs" color="red" style={{ maxWidth: 280 }}>
                            {formatMigrationJobError(job)}
                          </Text>
                        )}
                      </Stack>
                    </td>
                    <td>{formatMigrationDate(job.completedAt)}</td>
                    <td>
                      <Group spacing={6} noWrap>
                        <Button size="xs" variant="light" onClick={() => openDetail(job)}>
                          View
                        </Button>
                        {canStopMigrationJob(job) && (
                          <Button
                            size="xs"
                            color="orange"
                            loading={busyId === job._id}
                            onClick={() => handleStop(job)}
                          >
                            Stop
                          </Button>
                        )}
                        {canUndoMigrationJob(job) && (
                          <Button
                            size="xs"
                            color="red"
                            variant="light"
                            loading={busyId === job._id}
                            onClick={() => handleUndo(job)}
                          >
                            Undo
                          </Button>
                        )}
                      </Group>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {pages > 1 && (
            <Pagination size="sm" page={page} onChange={setPage} total={pages} />
          )}
        </Stack>
      </Card>

      <Modal
        opened={Boolean(detailJob)}
        onClose={() => setDetailJob(null)}
        title={detailJob ? `Rows · ${detailJob.code || detailJob.fileName}` : "Rows"}
        size="lg"
      >
        <Stack spacing="sm">
          {formatMigrationJobError(detailJob) && (
            <Text size="sm" color="red">
              {formatMigrationJobError(detailJob)}
            </Text>
          )}
          <Checkbox
            label="Errors only"
            checked={errorsOnly}
            onChange={(event) => handleErrorsOnly(event.currentTarget.checked)}
          />
          <Table fontSize="sm" striped>
            <thead>
              <tr>
                <th>#</th>
                <th>Code</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={2}>
                    <Text size="sm" color="dimmed">
                      No rows.
                    </Text>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={`${row.rowNumber}-${row.code}`}>
                    <td>{row.rowNumber}</td>
                    <td>
                      {row.code}
                      {row.status === "error" ||
                      row.status === "warning" ||
                      row.status === "skipped" ? (
                        <Text
                          size="xs"
                          color={
                            row.status === "error"
                              ? "red"
                              : row.status === "warning"
                                ? "orange"
                                : "dimmed"
                          }
                        >
                          {row.status === "skipped" ? "skipped" : ""}
                          {row.message
                            ? `${row.status === "skipped" ? ": " : ""}${row.message}`
                            : ""}
                        </Text>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          {rowPages > 1 && (
            <Pagination
              size="sm"
              page={rowPage}
              onChange={(next) => loadRows(detailJob, next, errorsOnly)}
              total={rowPages}
            />
          )}
        </Stack>
      </Modal>
    </>
  );
};

export default MigrationJobsPanel;
